import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminGetLeads, adminUpdateLead } from "@/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const STATUSES = ["New", "Contacted", "Qualified"];

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminGetLeads()
      .then(setLeads)
      .catch(() => toast.error("Could not load leads"))
      .finally(() => setLoading(false));
  }, []);

  const updateStatus = async (lead, status) => {
    try {
      await adminUpdateLead(lead.lead_id, status);
      setLeads((ls) => ls.map((l) => (l.lead_id === lead.lead_id ? { ...l, status } : l)));
      toast.success(`Marked ${lead.name} as ${status}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  if (loading) return <p className="text-sm text-muted-foreground" data-testid="admin-leads-loading">Loading leads…</p>;

  return (
    <div data-testid="admin-leads-page">
      <h1 className="text-3xl text-foreground">Leads</h1>
      <p className="mt-2 text-sm text-muted-foreground">{leads.length} enquiry{leads.length === 1 ? "" : "ies"} captured from the contact form.</p>
      <div className="mt-8 overflow-x-auto border border-border bg-card" data-testid="admin-leads-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Service</TableHead>
              <TableHead>Message</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {leads.map((l) => (
              <TableRow key={l.lead_id} data-testid={`admin-lead-row-${l.lead_id}`}>
                <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                  {new Date(l.created_at).toLocaleDateString("en-AE")}
                </TableCell>
                <TableCell className="font-medium">{l.name}</TableCell>
                <TableCell>{l.company || ","}</TableCell>
                <TableCell className="text-xs">{l.email}</TableCell>
                <TableCell className="text-xs">{l.phone || ","}</TableCell>
                <TableCell><Badge variant="outline" className="rounded-sm text-[10px]">{l.service}</Badge></TableCell>
                <TableCell className="max-w-[220px] truncate text-xs text-muted-foreground" title={l.message}>{l.message || ","}</TableCell>
                <TableCell>
                  <Select value={l.status} onValueChange={(v) => updateStatus(l, v)}>
                    <SelectTrigger className="h-8 w-[120px] rounded-sm text-xs" data-testid={`admin-lead-status-${l.lead_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`admin-lead-status-option-${s.toLowerCase()}`}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
            {leads.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="py-12 text-center text-sm text-muted-foreground" data-testid="admin-leads-empty">
                  No leads yet. New enquiries from the contact form appear here.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
